import type { RosterColumn } from '../../services/roster/roster.columns';
import { buildRosterCsv, rosterCsvFilename } from '../../services/roster/roster.csv';
import { rosterComingSoon } from '../../services/roster/roster.types';
import { ToastContext } from '../../services/toast/toast';
import { RosterExportIncompleteError, RosterStore } from '../../stores/roster/roster.store';
import { BaseButton } from '../base/button/button';
import { ComingSoon } from '../base/coming-soon/coming-soon';
import { downloadCsvBlob } from '../data-table/components/table-export/csv-utils';
import { rosterDisplayText } from './roster-display';
import { mdiDownload, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useContext, useState } from 'react';
import * as S from './roster-page.styles';

interface IRosterHeaderProps {
    store: RosterStore;
    /** The columns the table currently shows — the table's column picker owns them. */
    getVisibleColumns: () => RosterColumn[];
}

/**
 * Title, subtitle, and the two page actions: Download CSV and Add provider / Add facility.
 *
 * The CSV holds every record matching the current filters, not just the visible page, in the
 * columns currently shown — the design's `download()`. It reads display text, so the file matches
 * the screen. A search too broad to fetch in full downloads nothing rather than a partial file.
 */
export const RosterHeader = observer(({ store, getVisibleColumns }: IRosterHeaderProps): React.ReactElement => {
    const toast = useContext(ToastContext);
    const [exporting, setExporting] = useState(false);
    const isProviders = store.tab === 'providers';
    const addLabel = isProviders ? 'Add provider' : 'Add facility';

    const handleDownload = async (): Promise<void> => {
        const tab = store.tab;
        const columns = getVisibleColumns();
        setExporting(true);
        try {
            const records = await store.fetchAllMatching();
            const csv = buildRosterCsv(columns, records, (record, key) => rosterDisplayText(tab, record, key));
            downloadCsvBlob(csv, rosterCsvFilename(tab, new Date()));
            toast.notify?.(`${records.length} ${records.length === 1 ? 'row' : 'rows'} exported.`, 'right', 'light');
        } catch (error) {
            toast.notify?.(
                error instanceof RosterExportIncompleteError
                    ? 'This search matches more than 100 records in one field, so the export would be incomplete. Narrow the search and try again. Nothing was downloaded.'
                    : 'Export failed. Nothing was downloaded.',
                'right',
                'warning',
            );
        } finally {
            setExporting(false);
        }
    };

    // Shown either way; disabled under a "Coming soon" hint where the source cannot create yet (NS-4).
    const canCreate = store.dataSource.capabilities.write && !rosterComingSoon(store.dataSource.capabilities, 'create');
    const addButton = (
        <BaseButton variant="primary" onClick={store.openCreate} disabled={!canCreate}>
            <S.ButtonContent>
                <Icon path={mdiPlus} size={0.75} />
                {addLabel}
            </S.ButtonContent>
        </BaseButton>
    );

    return (
        <S.Header>
            <S.TitleBlock>
                <S.Title>Roster</S.Title>
                <S.Subtitle>
                    Providers and facilities on file for claim submission. Changes take effect on the next claim batch.
                </S.Subtitle>
            </S.TitleBlock>
            <S.HeaderActions>
                <BaseButton variant="outlined" onClick={handleDownload} disabled={exporting}>
                    <S.ButtonContent>
                        <Icon path={mdiDownload} size={0.75} />
                        {exporting ? 'Exporting…' : 'Download CSV'}
                    </S.ButtonContent>
                </BaseButton>
                {canCreate ? (
                    addButton
                ) : (
                    <ComingSoon hint={`adding a ${isProviders ? 'provider' : 'facility'} from this account`}>
                        {addButton}
                    </ComingSoon>
                )}
            </S.HeaderActions>
        </S.Header>
    );
});
